/*
  US English text swap. Loaded by layout/theme.liquid only on the US market;
  every other market never requests this file. See docs/us-english-text-swap.md.

  Replaces the UK spellings/terms the client listed with their US equivalents
  in visible text, wherever it came from — theme editor content, product
  descriptions, metafields, the cart drawer, search results. Most of that is
  merchant-edited and never passes through a Liquid filter we could hook, so
  it's done in the browser instead.

  Only text nodes and three label attributes (placeholder, aria-label, title)
  are ever touched. Never values, hrefs, option names, data attributes or
  `alt` (gallery photos are matched to variants through their alt text), so
  nothing the variant, cart or 3D-viewer logic reads can change underneath it.

  To add a word: add a rule to SPELLING_RULES (same word, US spelling) or
  TERM_RULES (a different word entirely).
*/
(function () {
  // Same word, different spelling. `stem` is the part that changes, `suffixes`
  // (and optional `prefixes`) are the endings known to be the same word. They
  // are listed explicitly because a bare stem match would also catch
  // "optimism" and "motorist". '' means the stem is a word on its own.
  const SPELLING_RULES = [
    { stem: 'colour', to: 'color', prefixes: ['dis'], suffixes: ['', 's', 'ed', 'ing', 'ful', 'less', 'ation'] },
    { stem: 'aluminium', to: 'aluminum', suffixes: ['', 's'] },
    { stem: 'centre', to: 'center', suffixes: ['', 's', 'd', 'piece', 'pieces'] },
    { stem: 'personalis', to: 'personaliz', suffixes: ['e', 'ed', 'es', 'ing', 'ation', 'ations'] },
    { stem: 'optimis', to: 'optimiz', suffixes: ['e', 'ed', 'es', 'er', 'ers', 'ing', 'ation', 'ations'] },
    { stem: 'motoris', to: 'motoriz', suffixes: ['e', 'ed', 'es', 'ing', 'ation', 'ations'] },
    { stem: 'authoris', to: 'authoriz', prefixes: ['un', 're'], suffixes: ['e', 'ed', 'es', 'ing', 'ation', 'ations'] },
  ];

  // Wording that is replaced with a different word. Only the exact word is
  // swapped, not "deliver"/"delivered"/"deliveries" — the client's list is the
  // noun only.
  const NEXT_TO_SHIPPING_BEFORE = /\bshipping\s*(?:&|\/|\+|and|,|-|–)?\s*$/i;
  const NEXT_TO_SHIPPING_AFTER = /^\s*(?:&|\/|\+|and|,|-|–)?\s*shipping\b/i;

  const TERM_RULES = [
    {
      word: 'delivery',
      re: /\bdelivery\b/gi,
      // Left alone beside "Shipping" ("Shipping & Delivery"), which is already
      // fine in US English and would otherwise read "Shipping & Shipping".
      to: (match, offset, string) => {
        const before = string.slice(Math.max(0, offset - 16), offset);
        const after = string.slice(offset + match.length, offset + match.length + 16);
        if ( NEXT_TO_SHIPPING_BEFORE.test(before) || NEXT_TO_SHIPPING_AFTER.test(after) ) return match;
        return matchCase(match, 'shipping');
      },
    },
    {
      word: 'postcode',
      re: /\bpostcodes?\b/gi,
      to: (match) => {
        const upper = isUpperCase(match);
        const plural = /s$/i.test(match);
        return (upper ? 'ZIP CODE' : 'ZIP code') + (plural ? (upper ? 'S' : 's') : '');
      },
    },
  ];

  // Text left exactly as written even when it contains a listed word: a real
  // postal address is a proper noun, and the regulator wording is quoted from
  // the regulator.
  const PROTECTED_PHRASES = [
    'Valley Centre',
    'authorised and regulated',
  ];

  const SKIP_ELEMENTS = new Set(['script', 'style', 'noscript', 'textarea', 'option', 'code', 'pre']);
  const OPT_OUT_SELECTOR = '[data-no-us-english]';
  const ATTRIBUTES = ['placeholder', 'aria-label', 'title'];
  const ATTRIBUTE_SELECTOR = ATTRIBUTES.map((name) => `[${name}]`).join(',');

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const isUpperCase = (value) => value.length > 1 && value === value.toUpperCase() && value !== value.toLowerCase();

  // Carries the original's capitalisation onto the replacement:
  // colour -> color, Colour -> Color, COLOUR -> COLOR.
  const matchCase = (original, replacement) => {
    if ( isUpperCase(original) ) return replacement.toUpperCase();
    if ( original.charAt(0) !== original.charAt(0).toLowerCase() ) return replacement.charAt(0).toUpperCase() + replacement.slice(1);
    return replacement;
  };

  const spellingRules = SPELLING_RULES.map((rule) => ({
    re: new RegExp(
      `\\b(${(rule.prefixes || []).length ? `(?:${rule.prefixes.join('|')})?` : ''})(${rule.stem})((?:${rule.suffixes.join('|')}))\\b`,
      'gi'
    ),
    to: (match, prefix, stem, suffix) => prefix + matchCase(stem, rule.to) + suffix,
  }));

  const rules = [...spellingRules, ...TERM_RULES];

  // Cheap test run on every text node first, so the (many) nodes with none of
  // the words cost one regex test and nothing else.
  const QUICK_CHECK = new RegExp([...SPELLING_RULES.map((rule) => rule.stem), ...TERM_RULES.map((rule) => rule.word)].join('|'), 'i');

  // split() with a capture group keeps the matched separators, at odd indexes.
  const PROTECTED_SPLIT = new RegExp(`(${PROTECTED_PHRASES.map(escapeRegExp).join('|')})`, 'i');

  const swapText = (text) => {
    if ( !QUICK_CHECK.test(text) ) return text;
    return text
      .split(PROTECTED_SPLIT)
      .map((part, index) => (index % 2 ? part : rules.reduce((current, rule) => current.replace(rule.re, rule.to), part)))
      .join('');
  };

  window.UsEnglishSwap = { swapText };

  // Everything below needs a DOM; the guard lets swapText be exercised on its own.
  if ( typeof document === 'undefined' ) return;

  const swapTextNode = (node) => {
    const value = node.nodeValue;
    if ( !QUICK_CHECK.test(value) ) return;
    const parent = node.parentElement;
    if ( !parent || SKIP_ELEMENTS.has(parent.localName) || parent.closest(OPT_OUT_SELECTOR) ) return;
    const swapped = swapText(value);
    if ( swapped !== value ) node.nodeValue = swapped;
  };

  const swapAttribute = (element, name) => {
    const value = element.getAttribute(name);
    if ( !value || !QUICK_CHECK.test(value) || element.closest(OPT_OUT_SELECTOR) ) return;
    const swapped = swapText(value);
    if ( swapped !== value ) element.setAttribute(name, swapped);
  };

  const swapNode = (node) => {
    if ( node.nodeType === Node.TEXT_NODE ) {
      swapTextNode(node);
      return;
    }
    if ( node.nodeType !== Node.ELEMENT_NODE ) return;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while ( walker.nextNode() ) swapTextNode(walker.currentNode);
    if ( node.matches(ATTRIBUTE_SELECTOR) ) ATTRIBUTES.forEach((name) => swapAttribute(node, name));
    node.querySelectorAll(ATTRIBUTE_SELECTOR).forEach((element) => ATTRIBUTES.forEach((name) => swapAttribute(element, name)));
  };

  // Whole document first (head included, so the tab title swaps too), then
  // anything added or changed afterwards: cart drawer, quick view, search,
  // filters, variant swaps. MutationObserver callbacks run before the next
  // paint, so late content never shows its UK wording first.
  swapNode(document.documentElement);

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if ( record.type === 'childList' ) record.addedNodes.forEach(swapNode);
      else if ( record.type === 'characterData' ) swapTextNode(record.target);
      else if ( record.type === 'attributes' ) swapAttribute(record.target, record.attributeName);
    });
    // Our own writes above are queued as records too; they have nothing left
    // to swap, so drop them rather than loop through them.
    observer.takeRecords();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRIBUTES,
  });

  document.documentElement.setAttribute('data-us-english', '');
})();
